import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import Button from '../ui/Button';
import { Gift, Users, Search, Database, ArrowRight, Loader2, Check, AlertCircle } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface ReferralInfo {
    valid: boolean;
    referral_code: string;
    referrer_name: string;
    bonus_amount: number;
    message: string;
}

const ReferralPage: React.FC = () => {
    const navigate = useNavigate();
    const { code } = useParams({ from: '/ref/$code' });
    const [referralInfo, setReferralInfo] = useState<ReferralInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (code) {
            fetchReferralInfo(code);
        }
    }, [code]);

    const fetchReferralInfo = async (refCode: string) => {
        try {
            const res = await fetch(`${API_URL}/referral/${refCode}`);
            const data = await res.json() as ReferralInfo;

            if (res.ok && data.valid) {
                setReferralInfo(data);
                // Store the referral code for use during registration
                localStorage.setItem('progeodata_referral_code', refCode);
            } else {
                setError('This referral link is no longer valid.');
            }
        } catch (err) {
            setError('Failed to load referral information.');
        } finally {
            setLoading(false);
        }
    };

    const handleSignUp = () => {
        navigate({ to: '/auth', search: { mode: 'register', redirect: '/dashboard' } });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center">
                    <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-white mb-2">Invalid Referral Link</h1>
                    <p className="text-slate-400 mb-6">{error}</p>
                    <Button variant="primary" onClick={() => navigate({ to: '/auth', search: { mode: 'register' } })}>
                        Sign Up Without Referral <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-900 py-12 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Referral Hero */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-500/20 rounded-full mb-6">
                        <Gift className="w-10 h-10 text-emerald-400" />
                    </div>
                    <h1 className="text-4xl font-bold text-white mb-4">
                        You've Been Invited!
                    </h1>
                    <p className="text-xl text-slate-300 mb-2">
                        {referralInfo?.referrer_name} invited you to <span className="text-sky-400 font-bold">ProGeoData</span>
                    </p>
                    <p className="text-slate-400">
                        The professional license data platform with 18M+ records
                    </p>
                </div>

                {/* Bonus Banner */}
                <div className="bg-gradient-to-r from-emerald-500/20 to-sky-500/20 border border-emerald-500/30 rounded-2xl p-8 mb-8 text-center">
                    <div className="text-6xl font-bold text-white mb-2">
                        50
                    </div>
                    <div className="text-emerald-400 font-bold text-lg mb-2">
                        Free Bonus Lookups
                    </div>
                    <p className="text-slate-300 text-sm">
                        Sign up now and get 50 bonus lookups on top of your 25 free monthly lookups
                    </p>
                </div>

                {/* Features */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 text-center">
                        <Database className="w-8 h-8 text-sky-400 mx-auto mb-3" />
                        <h3 className="text-white font-bold mb-2">18M+ Records</h3>
                        <p className="text-slate-400 text-sm">Professional license data across 15 states</p>
                    </div>
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 text-center">
                        <Search className="w-8 h-8 text-sky-400 mx-auto mb-3" />
                        <h3 className="text-white font-bold mb-2">25 Free Lookups</h3>
                        <p className="text-slate-400 text-sm">Every month, no credit card required</p>
                    </div>
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 text-center">
                        <Users className="w-8 h-8 text-sky-400 mx-auto mb-3" />
                        <h3 className="text-white font-bold mb-2">Refer & Earn</h3>
                        <p className="text-slate-400 text-sm">Get 50 bonus lookups for each referral</p>
                    </div>
                </div>

                {/* CTA */}
                <div className="text-center">
                    <Button
                        variant="primary"
                        className="text-lg px-8 py-4"
                        onClick={handleSignUp}
                    >
                        Claim Your 50 Free Lookups <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                    <p className="text-slate-500 text-sm mt-4">
                        No credit card required. Free plan includes 25 lookups + 50 bonus.
                    </p>
                </div>

                {/* How It Works */}
                <div className="mt-16 bg-slate-800 border border-slate-700 rounded-2xl p-8">
                    <h2 className="text-xl font-bold text-white mb-6 text-center">How It Works</h2>
                    <div className="space-y-6">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-sky-500 text-white rounded-full flex items-center justify-center font-bold">1</div>
                            <div>
                                <h3 className="text-white font-bold">Create your free account</h3>
                                <p className="text-slate-400 text-sm">Takes 30 seconds. No credit card needed.</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-sky-500 text-white rounded-full flex items-center justify-center font-bold">2</div>
                            <div>
                                <h3 className="text-white font-bold">Get 50 bonus lookups instantly</h3>
                                <p className="text-slate-400 text-sm">Your referral bonus is applied automatically at signup.</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-sky-500 text-white rounded-full flex items-center justify-center font-bold">3</div>
                            <div>
                                <h3 className="text-white font-bold">Search professional license data</h3>
                                <p className="text-slate-400 text-sm">Search by state, specialty, city. Export to CSV.</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold">
                                <Check className="w-4 h-4" />
                            </div>
                            <div>
                                <h3 className="text-white font-bold">Refer friends, earn more</h3>
                                <p className="text-slate-400 text-sm">Share your own referral link. Get 50 lookups for each signup.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReferralPage;
